import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/useMobile";
import { trpc } from "@/lib/trpc";
import { 
  Plus, Pencil, Clock, Loader2, Archive, ArchiveRestore, CalendarPlus, Calendar, Users, CheckCircle, XCircle, UserCheck, ChevronRight, ChevronDown, Trash2, Ban, Mail, MoreVertical, AlertTriangle, Search, UserPlus
} from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { CSSProperties, useEffect, useState } from "react";
import { Link } from "wouter";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { toast } from "sonner";
import { hasAnyRole } from "@/lib/roles";
import { CancelSessionDialog } from "@/components/CancelSessionDialog";
import { ReactivateSessionDialog } from "@/components/ReactivateSessionDialog";
import { BlockManagement } from "@/components/admin/BlockManagement";
import { useFeatures } from "@/components/FeatureGuard";
import { trackCourseCreated, trackCoursePublished, trackEvent, EVENTS } from "@/lib/tracking";


const DAYS_DE = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

const PAYMENT_CONFIG: Record<string, { label: string; className: string }> = {
  selbstzahler: { label: "Selbstzahler", className: "bg-slate-100 text-slate-700 border-slate-300" },
  krankenkasse: { label: "Krankenkasse", className: "bg-primary/10 text-primary border-primary/30" },
  erstattung: { label: "Erstattung möglich", className: "bg-orange-100 text-orange-700 border-orange-300" },
};

type CourseFormData = {
  name: string;
  description: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  paymentType: "selbstzahler" | "krankenkasse" | "erstattung";
  priceInCents?: number;
  capacity: number;
  category: string;
  instructorId?: number;
  isActive: boolean;
  waitlistEnabled: boolean;
  isBlockCourse: boolean;
  sessionsPerBlock?: number;
};

const defaultFormData: CourseFormData = {
  name: "",
  description: "",
  dayOfWeek: 1,
  startTime: "09:00",
  endTime: "10:00",
  paymentType: "selbstzahler",
  priceInCents: undefined,
  capacity: 10,
  category: "",
  instructorId: undefined,
  isActive: true,
  waitlistEnabled: false,
  isBlockCourse: false,
  sessionsPerBlock: undefined,
};

export default function AdminCourses() {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user || !hasAnyRole(user.roles, ["admin", "courseadmin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <h1 className="text-2xl font-semibold">Zugriff verweigert</h1>
          <Link href="/">
            <Button>Zur Startseite</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}>
      <AdminCoursesContent setSidebarWidth={setSidebarWidth} />
    </SidebarProvider>
  );
}

function AdminCoursesContent({ setSidebarWidth }: { setSidebarWidth: (width: number) => void }) {
  const isMobile = useIsMobile();
  const { features } = useFeatures();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<number | null>(null);
  const [formData, setFormData] = useState<CourseFormData>(defaultFormData);
  const [showArchived, setShowArchived] = useState(false);
  
  // Session generation state
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [sessionCourseId, setSessionCourseId] = useState<number | null>(null);
  const [sessionCourseName, setSessionCourseName] = useState<string>("");
  const [blockManagementCourseId, setBlockManagementCourseId] = useState<number | null>(null);
  const [blockManagementOpen, setBlockManagementOpen] = useState(false);
  const [generateStartDate, setGenerateStartDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [generateEndDate, setGenerateEndDate] = useState(() => {
    const future = new Date();
    future.setMonth(future.getMonth() + 3);
    return future.toISOString().split('T')[0];
  });

  // Sessions view state
  const [sessionsViewOpen, setSessionsViewOpen] = useState(false);
  const [viewSessionsCourseId, setViewSessionsCourseId] = useState<number | null>(null);
  const [viewSessionsCourseName, setViewSessionsCourseName] = useState("");
  const [viewSessionsBlockId, setViewSessionsBlockId] = useState<number | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  
  // Cancel session state
  const [cancelSessionId, setCancelSessionId] = useState<number | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [sendCancelEmail, setSendCancelEmail] = useState(true);
  
  // Reactivate session state
  const [reactivateSessionId, setReactivateSessionId] = useState<number | null>(null);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [sendReactivateEmail, setSendReactivateEmail] = useState(true);
  
  // Collapsible past sessions state
  const [showPastSessions, setShowPastSessions] = useState(false);

  // Add participant search state
  const [participantSearch, setParticipantSearch] = useState("");
  
  // Delete course state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteErrorDialogOpen, setDeleteErrorDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<{ id: number; name: string } | null>(null);
  const [checkingDelete, setCheckingDelete] = useState(false);
  
  // Archive course state
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [courseToArchive, setCourseToArchive] = useState<{ id: number; name: string } | null>(null);

  const utils = trpc.useUtils();
  const { data: courses, isLoading } = trpc.course.list.useQuery({ 
    includeInactive: true, 
    onlyArchived: showArchived 
  });
  const { data: instructors } = trpc.instructor.list.useQuery();

  // Sessions queries for the selected course (optionally filtered by block)
  const { data: courseSessions, isLoading: sessionsLoading, refetch: refetchSessions } = trpc.session.getByCourse.useQuery(
    { courseId: viewSessionsCourseId!, blockId: viewSessionsBlockId ?? undefined },
    { enabled: viewSessionsCourseId !== null && sessionsViewOpen }
  );
  const { data: sessionAttendance, isLoading: attendanceLoading, refetch: refetchAttendance } = trpc.session.getWithAttendance.useQuery(
    { sessionId: selectedSessionId!, courseId: viewSessionsCourseId! },
    { enabled: selectedSessionId !== null && viewSessionsCourseId !== null }
  );
  const { data: courseBookingsForSession, refetch: refetchCourseBookings } = trpc.session.getCourseBookingsForSession.useQuery(
    { sessionId: selectedSessionId!, courseId: viewSessionsCourseId!, search: participantSearch || undefined },
    { enabled: selectedSessionId !== null && viewSessionsCourseId !== null && participantSearch.length >= 2 }
  );
  const { data: detailedStats } = trpc.session.getDetailedStats.useQuery(
    { courseId: viewSessionsCourseId!, blockId: viewSessionsBlockId ?? undefined },
    { enabled: viewSessionsCourseId !== null && sessionsViewOpen }
  );

  const createMutation = trpc.course.create.useMutation({
    onSuccess: (result) => {
      // Track course creation
      trackCourseCreated(result.id, formData.name, formData.isBlockCourse);
      
      // Track course published if created as active
      if (formData.isActive) {
        trackCoursePublished(result.id, formData.name, formData.isBlockCourse);
      }
      
      toast.success("Kurs erstellt");
      utils.course.list.invalidate();
      setDialogOpen(false);
      setFormData(defaultFormData);
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = trpc.course.update.useMutation({
    onSuccess: () => {
      // Track course update
      trackEvent(EVENTS.COURSE_UPDATED, { course_id: editingCourse?.id, course_name: formData.name });
      
      // Track course published if toggled from inactive to active
      if (formData.isActive && editingCourse && !editingCourse.isActive) {
        trackCoursePublished(editingCourse.id, formData.name, formData.isBlockCourse);
      }
      
      toast.success("Kurs aktualisiert");
      utils.course.list.invalidate();
      setDialogOpen(false);
      setEditingCourse(null);
      setFormData(defaultFormData);
    },
    onError: (error) => toast.error(error.message),
  });

  const archiveMutation = trpc.course.archive.useMutation({
    onSuccess: () => {
      toast.success("Kurs archiviert");
      utils.course.list.invalidate();
      setArchiveDialogOpen(false);
      setCourseToArchive(null);
    },
    onError: (error) => toast.error(error.message),
  });

  const restoreMutation = trpc.course.restore.useMutation({
    onSuccess: () => {
      toast.success("Kurs wiederhergestellt");
      utils.course.list.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteMutation = trpc.course.delete.useMutation({
    onSuccess: () => {
      toast.success("Kurs gelöscht");
      utils.course.list.invalidate();
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
    },
    onError: (error) => {
      setDeleteDialogOpen(false);
      if (error.message.includes("Anmeldungen")) {
        setDeleteErrorDialogOpen(true);
      } else {
        toast.error(error.message);
        setCourseToDelete(null);
      }
    },
  });

  // Function to check if course can be deleted before showing dialog
  const handleDeleteClick = async (course: { id: number; name: string }) => {
    setCourseToDelete(course);
    setCheckingDelete(true);
    try {
      const result = await utils.course.checkCanDelete.fetch({ id: course.id });
      if (result.canDelete) {
        setDeleteDialogOpen(true);
      } else {
        setDeleteErrorDialogOpen(true);
      }
    } catch (error) {
      toast.error("Fehler beim Prüfen der Löschbarkeit");
      setCourseToDelete(null);
    } finally {
      setCheckingDelete(false);
    }
  };

  const generateSessionsMutation = trpc.session.generateForCourse.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.count} Termine erstellt`);
      setSessionDialogOpen(false);
      setSessionCourseId(null);
      refetchSessions();
    },
    onError: (error) => toast.error(error.message),
  });

  const markAttendanceMutation = trpc.session.markAttendance.useMutation({
    onSuccess: () => {
      refetchAttendance();
      toast.success("Anwesenheit aktualisiert");
    },
    onError: () => toast.error("Fehler beim Speichern der Anwesenheit"),
  });

  const addParticipantMutation = trpc.session.addParticipantToSession.useMutation({
    onSuccess: () => {
      toast.success("Teilnehmer hinzugefügt");
      refetchAttendance();
      refetchCourseBookings();
      setParticipantSearch("");
    },
    onError: (e) => toast.error("Fehler", { description: e.message }),
  });

  const cancelSessionMutation = trpc.session.update.useMutation({
    onSuccess: (data) => {
      refetchSessions();
      setCancelDialogOpen(false);
      setCancelSessionId(null);
      setCancelReason("");
      if (data.emailsSent > 0) {
        toast.success(`Termin abgesagt. ${data.emailsSent} E-Mail(s) versendet.`);
      } else {
        toast.success("Termin abgesagt");
      }
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteSessionMutation = trpc.session.delete.useMutation({
    onSuccess: () => {
      refetchSessions();
      toast.success("Termin gelöscht");
    },
    onError: (error) => toast.error(error.message),
  });

  const reactivateSessionMutation = trpc.session.update.useMutation({
    onSuccess: (data) => {
      refetchSessions();
      setReactivateDialogOpen(false);
      setReactivateSessionId(null);
      if (data.emailsSent > 0) {
        toast.success(`Termin reaktiviert. ${data.emailsSent} E-Mail(s) versendet.`);
      } else {
        toast.success("Termin reaktiviert");
      }
    },
    onError: (error) => toast.error(error.message),
  });

  // Helper functions for date formatting (short format for mobile: Mo. 19.12.25)
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("de-DE", { 
      weekday: "short", 
      day: "2-digit", 
      month: "2-digit",
      year: "2-digit"
    });
  };

  const isPastDate = (dateStr: string): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sessionDate = new Date(dateStr + "T00:00:00");
    return sessionDate < today;
  };

  const isToday = (dateStr: string): boolean => {
    const today = new Date();
    const sessionDate = new Date(dateStr + "T00:00:00");
    return today.toDateString() === sessionDate.toDateString();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      ...formData,
      instructorId: formData.instructorId || undefined,
      priceInCents: formData.priceInCents || undefined,
    };

    if (editingCourse) {
      updateMutation.mutate({ id: editingCourse, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const openEditDialog = (course: NonNullable<typeof courses>[0]) => {
    setEditingCourse(course.id);
    setFormData({
      name: course.name,
      description: course.description || "",
      dayOfWeek: course.dayOfWeek,
      startTime: course.startTime,
      endTime: course.endTime,
      paymentType: course.paymentType,
      priceInCents: course.priceInCents || undefined,
      capacity: course.capacity,
      category: course.category || "",
      instructorId: course.instructorId || undefined,
      isActive: course.isActive,
      waitlistEnabled: course.waitlistEnabled,
      isBlockCourse: course.isBlockCourse || false,
      sessionsPerBlock: course.sessionsPerBlock || undefined,
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingCourse(null);
    setFormData(defaultFormData);
    setDialogOpen(true);
  };

  return (
    <>
      <AdminSidebar setSidebarWidth={setSidebarWidth} />

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">Kurse</span>
            </div>
          </div>
        )}
        <main className="flex-1 p-6">
          <div className="flex flex-col gap-4 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Kursverwaltung</h1>
                <p className="text-muted-foreground">Erstellen und verwalten Sie Ihre Kurse</p>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateDialog} className="gap-2 bg-primary hover:bg-primary/90 w-full sm:w-auto">
                  <Plus className="h-4 w-4" />
                  <span>Neuer Kurs</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingCourse ? "Kurs bearbeiten" : "Neuen Kurs erstellen"}</DialogTitle>
                  <DialogDescription>
                    {editingCourse ? "Bearbeiten Sie die Kursdetails" : "Füllen Sie das Formular aus, um einen neuen Kurs zu erstellen"}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="name">Kursname *</Label>
                      <Input id="name" required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="z.B. Yoga für Anfänger" />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="description">Beschreibung</Label>
                      <Textarea id="description" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Kursbeschreibung..." rows={4} />
                    </div>
                    <div className="space-y-2">
                      <Label>Wochentag *</Label>
                      <Select value={formData.dayOfWeek.toString()} onValueChange={v => setFormData(p => ({ ...p, dayOfWeek: parseInt(v) }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5].map(d => (
                            <SelectItem key={d} value={d.toString()}>{DAYS_DE[d]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Kursleiter</Label>
                      <Select value={formData.instructorId?.toString() || "none"} onValueChange={v => setFormData(p => ({ ...p, instructorId: v === "none" ? undefined : parseInt(v) }))}>
                        <SelectTrigger><SelectValue placeholder="Kursleiter wählen" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Kein Kursleiter</SelectItem>
                          {instructors?.map(i => (
                            <SelectItem key={i.id} value={i.id.toString()}>{i.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="startTime">Startzeit *</Label>
                      <Input id="startTime" type="time" required value={formData.startTime} onChange={e => setFormData(p => ({ ...p, startTime: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endTime">Endzeit *</Label>
                      <Input id="endTime" type="time" required value={formData.endTime} onChange={e => setFormData(p => ({ ...p, endTime: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Zahlungsart *</Label>
                      <Select value={formData.paymentType} onValueChange={(v: "selbstzahler" | "krankenkasse" | "erstattung") => setFormData(p => ({ ...p, paymentType: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="selbstzahler">Selbstzahler</SelectItem>
                          <SelectItem value="krankenkasse">Krankenkasse</SelectItem>
                          <SelectItem value="erstattung">Erstattung möglich</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="price">Preis (€)</Label>
                      <Input id="price" type="number" step="0.01" min="0" value={formData.priceInCents ? (formData.priceInCents / 100).toString() : ""} onChange={e => setFormData(p => ({ ...p, priceInCents: e.target.value ? Math.round(parseFloat(e.target.value) * 100) : undefined }))} placeholder="0.00" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="capacity">Kapazität *</Label>
                      <Input id="capacity" type="number" min="1" required value={formData.capacity} onChange={e => setFormData(p => ({ ...p, capacity: parseInt(e.target.value) || 10 }))} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Kategorie</Label>
                      <Input id="category" value={formData.category} onChange={e => setFormData(p => ({ ...p, category: e.target.value }))} placeholder="z.B. Yoga, Fitness" />
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch id="isActive" checked={formData.isActive} onCheckedChange={v => setFormData(p => ({ ...p, isActive: v }))} />
                      <Label htmlFor="isActive">Kurs aktiv</Label>
                    </div>
                    {features.blockCoursesEnabled && (
                      <div className="flex items-center gap-3">
                        <Switch id="isBlockCourse" checked={formData.isBlockCourse} onCheckedChange={v => setFormData(p => ({ ...p, isBlockCourse: v, sessionsPerBlock: v ? (p.sessionsPerBlock || 8) : undefined }))} />
                        <Label htmlFor="isBlockCourse">Blockkurs</Label>
                      </div>
                    )}
                    {!!features.waitlistEnabled && !formData.isBlockCourse && (
                      <div className="flex items-center gap-3">
                        <Switch id="waitlistEnabled" checked={formData.waitlistEnabled} onCheckedChange={v => setFormData(p => ({ ...p, waitlistEnabled: v }))} />
                        <Label htmlFor="waitlistEnabled">Warteliste aktivieren</Label>
                      </div>
                    )}
                    {features.blockCoursesEnabled && formData.isBlockCourse && (
                      <div className="space-y-2">
                        <Label htmlFor="sessionsPerBlock">Termine pro Block *</Label>
                        <Input id="sessionsPerBlock" type="number" min="1" required value={formData.sessionsPerBlock || 8} onChange={e => setFormData(p => ({ ...p, sessionsPerBlock: parseInt(e.target.value) || 8 }))} />
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="bg-primary hover:bg-primary/90">
                      {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {editingCourse ? "Speichern" : "Erstellen"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="showArchived"
                checked={showArchived}
                onCheckedChange={setShowArchived}
              />
              <Label htmlFor="showArchived" className="text-sm text-muted-foreground cursor-pointer">
                Archivierte Kurse anzeigen
              </Label>
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Card key={i}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-1/3" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                      <Skeleton className="h-8 w-20" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : courses?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground mb-4">
                  {showArchived ? "Keine archivierten Kurse vorhanden" : "Noch keine Kurse vorhanden"}
                </p>
                {!showArchived && (
                  <Button onClick={openCreateDialog}>Ersten Kurs erstellen</Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {courses?.map(course => (
                <Card key={course.id} className="relative">
                  <CardContent className="py-4">
                    {/* Three-dot menu in top right */}
                    <div className="absolute top-2 right-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(course)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Bearbeiten
                          </DropdownMenuItem>
                          {course.isArchived ? (
                            <DropdownMenuItem onClick={() => restoreMutation.mutate({ id: course.id })}>
                              <ArchiveRestore className="h-4 w-4 mr-2" />
                              Wiederherstellen
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => {
                                setCourseToArchive({ id: course.id, name: course.name });
                                setArchiveDialogOpen(true);
                              }}
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archivieren
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteClick({ id: course.id, name: course.name })}
                            className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/50"
                            disabled={checkingDelete}
                          >
                            <Trash2 className="h-4 w-4 mr-2 text-red-600" />
                            {checkingDelete ? "Prüfe..." : "Löschen"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Row 1: Course info */}
                    <div className="mb-3 pr-10">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold">{course.name}</h3>
                        {!course.isActive && <Badge variant="outline">Inaktiv</Badge>}
                        {!!course.isArchived && <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">Archiviert</Badge>}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span>{DAYS_DE[course.dayOfWeek]}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {course.startTime} - {course.endTime}
                        </span>
                        <Badge variant="outline" className={PAYMENT_CONFIG[course.paymentType]?.className}>
                          {PAYMENT_CONFIG[course.paymentType]?.label}
                        </Badge>
                        {course.instructor && <span>• {course.instructor.name}</span>}
                      </div>
                    </div>
                    {/* Row 2: Action buttons - right-aligned, icon only on mobile, with labels on larger screens */}
                    <div className="flex items-center justify-end gap-1 sm:gap-2 pt-2 border-t flex-wrap">
                        {!!course.isBlockCourse && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 px-2 sm:px-3 text-primary"
                            onClick={() => {
                              setBlockManagementCourseId(course.id);
                              setBlockManagementOpen(true);
                            }}
                            title="Blöcke verwalten"
                          >
                            <Users className="h-4 w-4" />
                            <span className="hidden sm:inline ml-1.5">Blöcke</span>
                          </Button>
                        )}
                        {/* Termine button - only for non-block courses */}
                        {!course.isBlockCourse && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 px-2 sm:px-3"
                            onClick={() => {
                              setViewSessionsCourseId(course.id);
                              setViewSessionsCourseName(course.name);
                              setViewSessionsBlockId(null);
                              setSelectedSessionId(null);
                              setSessionsViewOpen(true);
                            }}
                            title="Termine anzeigen"
                          >
                            <Calendar className="h-4 w-4" />
                            <span className="hidden sm:inline ml-1.5">Termine</span>
                          </Button>
                        )}
                        <Link href={`/admin/bookings?courseId=${course.id}`}>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 px-2 sm:px-3"
                            title="Anmeldungen anzeigen"
                          >
                            <UserCheck className="h-4 w-4" />
                            <span className="hidden sm:inline ml-1.5">Anmeldungen</span>
                          </Button>
                        </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
      </SidebarInset>

      {/* Delete Course Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) setCourseToDelete(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kurs löschen?</DialogTitle>
            <DialogDescription>
              Möchten Sie den Kurs "{courseToDelete?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeleteDialogOpen(false);
              setCourseToDelete(null);
            }}>
              Abbrechen
            </Button>
            <Button 
              variant="destructive"
              onClick={() => courseToDelete && deleteMutation.mutate({ id: courseToDelete.id })}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Löschen...</>
              ) : (
                "Löschen"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Course Error Dialog (has bookings) */}
      <Dialog open={deleteErrorDialogOpen} onOpenChange={(open) => {
        setDeleteErrorDialogOpen(open);
        if (!open) setCourseToDelete(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Kurs kann nicht gelöscht werden
            </DialogTitle>
            <DialogDescription>
              Der Kurs "{courseToDelete?.name}" hat noch Anmeldungen und kann daher nicht gelöscht werden. Bitte stornieren Sie zuerst alle Anmeldungen oder archivieren Sie den Kurs stattdessen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => {
              setDeleteErrorDialogOpen(false);
              setCourseToDelete(null);
            }}>
              Verstanden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Course Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={(open) => {
        setArchiveDialogOpen(open);
        if (!open) setCourseToArchive(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kurs archivieren?</DialogTitle>
            <DialogDescription>
              Möchten Sie den Kurs "{courseToArchive?.name}" wirklich archivieren? Der Kurs wird nicht mehr in der öffentlichen Kursliste angezeigt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setArchiveDialogOpen(false);
              setCourseToArchive(null);
            }}>
              Abbrechen
            </Button>
            <Button 
              onClick={() => courseToArchive && archiveMutation.mutate({ id: courseToArchive.id })}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Archivieren...</>
              ) : (
                "Archivieren"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Generation Dialog */}
      <Dialog open={sessionDialogOpen} onOpenChange={setSessionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Termine generieren</DialogTitle>
            <DialogDescription>
              Erstellen Sie automatisch Termine für den Kurs "{sessionCourseName}" basierend auf dem Wochentag.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="startDate">Startdatum</Label>
              <Input
                id="startDate"
                type="date"
                value={generateStartDate}
                onChange={(e) => setGenerateStartDate(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="endDate">Enddatum</Label>
              <Input
                id="endDate"
                type="date"
                value={generateEndDate}
                onChange={(e) => setGenerateEndDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSessionDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={() => {
                if (sessionCourseId) {
                  generateSessionsMutation.mutate({
                    courseId: sessionCourseId,
                    startDate: generateStartDate,
                    endDate: generateEndDate,
                  });
                }
              }}
              disabled={generateSessionsMutation.isPending}
              className="bg-primary hover:bg-primary/90"
            >
              {generateSessionsMutation.isPending ? "Generiere..." : "Generieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sessions View Dialog */}
      <Dialog open={sessionsViewOpen} onOpenChange={(open) => {
        setSessionsViewOpen(open);
        if (!open) {
          setSelectedSessionId(null);
        }
      }}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="space-y-2 pb-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                {selectedSessionId ? (
                  <>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedSessionId(null)}>
                      <ChevronRight className="h-4 w-4 rotate-180" />
                    </Button>
                    Anwesenheit - {formatDate(courseSessions?.find(s => s.id === selectedSessionId)?.sessionDate || "")}
                  </>
                ) : (
                  <>
                    <Calendar className="h-5 w-5" />
                    Termine: {viewSessionsCourseName}
                    {viewSessionsBlockId && (
                      <Badge variant="outline" className="ml-2">Block gefiltert</Badge>
                    )}
                  </>
                )}
              </DialogTitle>
              {/* Generate button - only show when not viewing attendance and not filtered by block */}
              {!selectedSessionId && !viewSessionsBlockId && viewSessionsCourseId && (
                <Button
                  size="sm"
                  className="mr-6"
                  onClick={() => {
                    setSessionCourseId(viewSessionsCourseId);
                    setSessionCourseName(viewSessionsCourseName);
                    setSessionDialogOpen(true);
                  }}
                >
                  <CalendarPlus className="h-4 w-4 mr-1.5" />
                  Generieren
                </Button>
              )}
            </div>
            <DialogDescription>
              {selectedSessionId 
                ? "Markieren Sie die Anwesenheit der Teilnehmer."
                : viewSessionsBlockId
                  ? `${courseSessions?.length || 0} Termine für diesen Block. Klicken Sie auf einen Termin, um die Anwesenheit zu erfassen.`
                  : `${courseSessions?.length || 0} Termine gefunden. Klicken Sie auf einen Termin, um die Anwesenheit zu erfassen.`
              }
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto">
            {selectedSessionId ? (
              // Attendance View
              <div className="space-y-4">
                {attendanceLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : sessionAttendance?.attendance.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Keine Teilnehmer für diesen Termin</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Teilnehmer</TableHead>
                        <TableHead>E-Mail</TableHead>
                        <TableHead className="text-center">Anwesend</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessionAttendance?.attendance.map((record) => (
                        <TableRow key={record.booking.id}>
                          <TableCell className="font-medium">{record.booking.participantName}</TableCell>
                          <TableCell className="text-muted-foreground">{record.booking.participantEmail}</TableCell>
                          <TableCell className="text-center">
                            <Checkbox
                              checked={record.attended === true}
                              onCheckedChange={(checked) => {
                                markAttendanceMutation.mutate({
                                  sessionId: selectedSessionId,
                                  bookingId: record.booking.id,
                                  attended: checked === true,
                                });
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {/* Add participant from course bookings */}
                <div className="pt-4 border-t space-y-2">
                  <p className="text-sm font-medium">Teilnehmer hinzufügen</p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Name oder E-Mail suchen..."
                        value={participantSearch}
                        onChange={(e) => setParticipantSearch(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>
                  {participantSearch.length >= 2 && courseBookingsForSession && courseBookingsForSession.length > 0 && (
                    <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                      {courseBookingsForSession.map((b) => (
                        <div key={b.id} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div>
                            <span className="font-medium">{b.participantName}</span>
                            <span className="text-muted-foreground ml-2">{b.participantEmail}</span>
                          </div>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => addParticipantMutation.mutate({ sessionId: selectedSessionId!, courseBookingId: b.id })}
                            disabled={addParticipantMutation.isPending}
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {participantSearch.length >= 2 && courseBookingsForSession?.length === 0 && (
                    <p className="text-sm text-muted-foreground">Keine Kursbuchungen gefunden.</p>
                  )}
                </div>

                {/* Attendance Summary */}
                {sessionAttendance && sessionAttendance.attendance.length > 0 && (
                  <div className="flex gap-4 pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>{sessionAttendance.attendance.filter(r => r.attended === true).length} Anwesend</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <XCircle className="h-4 w-4 text-red-600" />
                      <span>{sessionAttendance.attendance.filter(r => r.attended === false).length} Abwesend</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{sessionAttendance.attendance.filter(r => r.attended === null).length} Nicht erfasst</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Sessions List View
              <div className="space-y-4">
                {/* Stats Overview */}
                {detailedStats && detailedStats.overall.totalSessions > 0 && (
                  <div className="grid grid-cols-3 gap-2 px-2 mt-4 mb-2">
                    <Card>
                      <CardContent className="p-3 text-center">
                        <div className="text-xl font-bold">{detailedStats.overall.totalSessions}</div>
                        <div className="text-xs text-muted-foreground">Termine</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-3 text-center">
                        <div className="text-xl font-bold text-green-600">{detailedStats.overall.averageAttendance}%</div>
                        <div className="text-xs text-muted-foreground">Ø Anwes.</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-3 text-center">
                        <div className="text-xl font-bold">{detailedStats.overall.totalPresent}</div>
                        <div className="text-xs text-muted-foreground">Anwesend</div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {sessionsLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : courseSessions?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Keine Termine vorhanden</p>
                    <p className="text-sm mt-1">Generieren Sie Termine über den Kalender-Button.</p>
                  </div>
                ) : (() => {
                  const upcomingSessions = courseSessions?.filter(s => !isPastDate(s.sessionDate)) || [];
                  const pastSessions = courseSessions?.filter(s => isPastDate(s.sessionDate)) || [];
                  
                  const renderSessionCard = (session: NonNullable<typeof courseSessions>[number]) => {
                    const stats = detailedStats?.sessions.find(s => s.id === session.id);
                    const past = isPastDate(session.sessionDate);
                    const today = isToday(session.sessionDate);
                    const hasAttendanceRecords = stats && stats.totalParticipants > 0 && (stats.presentCount > 0 || stats.absentCount > 0);
                    
                    return (
                      <div 
                        key={session.id}
                        className={`p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors ${session.isCancelled ? 'opacity-60' : ''}`}
                      >
                        {/* Row 1: Status badge and date */}
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            {session.isCancelled ? (
                              <Badge variant="destructive" className="shrink-0">Abgesagt</Badge>
                            ) : today ? (
                              <Badge className="bg-green-600 shrink-0">Heute</Badge>
                            ) : past ? (
                              <Badge variant="secondary" className="shrink-0">Vergangen</Badge>
                            ) : (
                              <Badge variant="outline" className="shrink-0">Geplant</Badge>
                            )}
                            <span className="font-medium">{formatDate(session.sessionDate)}</span>
                          </div>
                        </div>
                        {/* Attendance progress bar */}
                        {stats && stats.totalParticipants > 0 && (
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-500 rounded-full"
                                style={{ width: `${stats.attendanceRate}%` }}
                              />
                            </div>
                            <span className="text-sm text-muted-foreground shrink-0">
                              {stats.presentCount}/{stats.totalParticipants}
                            </span>
                          </div>
                        )}
                        {/* Row 2: Action buttons - right-aligned, icon only on mobile, with labels on desktop */}
                        <div className="flex items-center justify-end gap-1 sm:gap-2 pt-2 border-t">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 sm:px-3"
                            onClick={() => setSelectedSessionId(session.id)}
                            title="Anwesenheit"
                          >
                            <UserCheck className="h-4 w-4" />
                            <span className="hidden sm:inline ml-1.5">Anwesenheit</span>
                          </Button>
                          {!session.isCancelled && !past && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 sm:px-3 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              onClick={() => {
                                setCancelSessionId(session.id);
                                setCancelDialogOpen(true);
                              }}
                              title="Absagen"
                            >
                              <Ban className="h-4 w-4" />
                              <span className="hidden sm:inline ml-1.5">Absagen</span>
                            </Button>
                          )}
                          {!!session.isCancelled && !past && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 sm:px-3 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => {
                                setReactivateSessionId(session.id);
                                setReactivateDialogOpen(true);
                              }}
                              title="Reaktivieren"
                            >
                              <ArchiveRestore className="h-4 w-4" />
                              <span className="hidden sm:inline ml-1.5">Reaktivieren</span>
                            </Button>
                          )}
                          {!hasAttendanceRecords && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 sm:px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Löschen"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  <span className="hidden sm:inline ml-1.5">Löschen</span>
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Termin löschen?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Möchten Sie den Termin am {formatDate(session.sessionDate)} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-red-600 hover:bg-red-700"
                                    onClick={() => deleteSessionMutation.mutate({ id: session.id })}
                                  >
                                    Löschen
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </div>
                    );
                  };
                  
                  return (
                    <div className="px-2 py-3 space-y-2">
                      {/* Upcoming sessions */}
                      {upcomingSessions.map(renderSessionCard)}
                      
                      {/* Past sessions collapsible */}
                      {pastSessions.length > 0 && (
                        <div className="mt-4">
                          <button
                            onClick={() => setShowPastSessions(!showPastSessions)}
                            className="flex items-center gap-2 w-full p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                          >
                            {showPastSessions ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <span>Vergangene Termine ({pastSessions.length})</span>
                          </button>
                          {showPastSessions && (
                            <div className="mt-2 space-y-2">
                              {pastSessions.map(renderSessionCard)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          
          {/* Close button at bottom */}
          <div className="sticky bottom-0 p-3 border-t bg-background">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => {
                setSessionsViewOpen(false);
                setViewSessionsCourseId(null);
                setViewSessionsBlockId(null);
                setSelectedSessionId(null);
              }}
            >
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Session Dialog */}
      <CancelSessionDialog
        open={cancelDialogOpen}
        onOpenChange={(open) => {
          setCancelDialogOpen(open);
          if (!open) setCancelSessionId(null);
        }}
        onConfirm={({ reason, sendEmail }) => {
          if (cancelSessionId) {
            cancelSessionMutation.mutate({
              id: cancelSessionId,
              isCancelled: true,
              cancellationReason: reason,
              sendCancellationEmail: sendEmail,
            });
          }
        }}
        isPending={cancelSessionMutation.isPending}
      />

      {/* Reactivate Session Dialog */}
      <ReactivateSessionDialog
        open={reactivateDialogOpen}
        onOpenChange={(open) => {
          setReactivateDialogOpen(open);
          if (!open) setReactivateSessionId(null);
        }}
        onConfirm={({ sendEmail }) => {
          if (reactivateSessionId) {
            reactivateSessionMutation.mutate({
              id: reactivateSessionId,
              isCancelled: false,
              sendReactivationEmail: sendEmail,
            });
          }
        }}
        isPending={reactivateSessionMutation.isPending}
      />

      {/* Block Management Dialog */}
      <Dialog open={blockManagementOpen} onOpenChange={setBlockManagementOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Kursblöcke verwalten</DialogTitle>
            <DialogDescription>
              Verwalten Sie die Blöcke für diesen Kurs. Jeder Block hat eigene Termine und Teilnehmer.
            </DialogDescription>
          </DialogHeader>
          {blockManagementCourseId && (
            <BlockManagement 
              courseId={blockManagementCourseId}
              courseCapacity={courses?.find(c => c.id === blockManagementCourseId)?.capacity}
              onOpenSessions={(blockId) => {
                // Close block management and open sessions view filtered by block
                setBlockManagementOpen(false);
                setViewSessionsCourseId(blockManagementCourseId);
                setViewSessionsBlockId(blockId);
                setViewSessionsCourseName(courses?.find(c => c.id === blockManagementCourseId)?.name || "");
                setSelectedSessionId(null);
                setSessionsViewOpen(true);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
